import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, getImageUrl } from "../api";
import { useStore } from "../store";
import { enablePush } from "../push";

const FREE_SHIPPING_THRESHOLD = 99;

export default function Cart() {
  const { cart, updateQty, removeFromCart, clearCart, cartTotal, customer } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: customer?.name || "",
    customer_email: customer?.email || "",
    customer_phone: "",
    address: "",
    city: "",
    zip_code: "",
    notes: "",
  });
  const [placing, setPlacing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  // Pre-compila email e nome se l'utente accede dopo essere entrato nella pagina
  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        customer_email: f.customer_email || customer.email || "",
        customer_name: f.customer_name || customer.name || "",
      }));
    }
  }, [customer]);

  const shipping = cartTotal >= FREE_SHIPPING_THRESHOLD || cartTotal === 0 ? 0 : 7.9;
  const total = cartTotal + shipping;

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!cart.length) return;
    setPlacing(true);
    try {
      await api.post("/orders", { ...form, items: cart, total });
      localStorage.setItem("dorae_customer_email", form.customer_email.trim().toLowerCase());
      enablePush().catch(() => {});
      setOrderPlaced(true);
      clearCart();
    } catch {
      toast.error("Non è stato possibile inviare l'ordine. Riprova.", { position: "top-center" });
    } finally {
      setPlacing(false);
    }
  };

  if (orderPlaced) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="order-success">
        <h1 className="font-display text-3xl text-[#2C2C2C]">Grazie!</h1>
        <p className="mt-4 text-sm leading-relaxed text-[#6B6357] tracking-wide">
          Il tuo ordine è stato ricevuto. Ti contatteremo al più presto per conferma e dettagli del pagamento.
        </p>
        <Link to="/" data-testid="order-success-home" className="inline-block mt-8 gold-gradient text-white px-10 py-3.5 text-[12px] font-semibold tracking-wide-spaced uppercase">
          Torna alla home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pb-24" data-testid="cart-page">
      <div className="flex items-center gap-3 pt-5 pb-2">
        <button data-testid="back-button" onClick={() => navigate(-1)} aria-label="Torna indietro" className="p-2 -ml-2 text-[#2C2C2C] hover:opacity-60 transition-opacity duration-200">
          <ArrowLeft size={20} strokeWidth={1.5} />
        </button>
      </div>
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl text-[#2C2C2C]" data-testid="cart-title">Il tuo carrello</h1>
        <div className="gold-line w-24 mx-auto mt-4" />
      </div>

      {!cart.length ? (
        <div className="text-center py-14" data-testid="cart-empty">
          <p className="text-sm tracking-wide text-[#8B8578]">Il carrello è vuoto.</p>
          <Link to="/categoria/nuovi-arrivi" data-testid="cart-cta" className="inline-block mt-6 gold-gradient text-white px-10 py-3.5 text-[12px] font-semibold tracking-wide-spaced uppercase">
            Acquista ora
          </Link>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-[#EDE6DB]" data-testid="cart-items">
            {cart.map((item) => (
              <li key={item.product_id} className="flex gap-4 py-5" data-testid={`cart-item-${item.product_id}`}>
                <img src={getImageUrl(item.image_url)} alt={item.name} className="w-20 h-24 object-cover bg-white" />
                <div className="flex-1">
                  <p className="font-display text-base text-[#2C2C2C]">{item.name}</p>
                  <p className="mt-1 text-sm font-semibold gold-gradient-text">€ {item.price.toFixed(2)}</p>
                  <div className="mt-3 flex items-center border border-[#EAE4DA] w-fit">
                    <button data-testid={`qty-minus-${item.product_id}`} onClick={() => updateQty(item.product_id, -1)} className="p-2 text-[#2C2C2C]" aria-label="Riduci quantità">
                      <Minus size={14} strokeWidth={1.5} />
                    </button>
                    <span className="w-8 text-center text-sm" data-testid={`qty-${item.product_id}`}>{item.quantity}</span>
                    <button data-testid={`qty-plus-${item.product_id}`} onClick={() => updateQty(item.product_id, 1)} className="p-2 text-[#2C2C2C]" aria-label="Aumenta quantità">
                      <Plus size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                <button data-testid={`remove-${item.product_id}`} onClick={() => removeFromCart(item.product_id)} aria-label="Rimuovi" className="p-2 h-fit text-[#B0A898] hover:text-[#8B5A2B] transition-colors">
                  <Trash2 size={17} strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-6 border-t border-[#EAE4DA] pt-6 space-y-2 text-sm" data-testid="cart-summary">
            <div className="flex justify-between text-[#6B6357]">
              <span>Subtotale</span>
              <span data-testid="cart-subtotal">€ {cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[#6B6357]">
              <span>Spedizione {shipping === 0 && cartTotal > 0 && <em className="text-[#AA7C11] not-italic">(gratuita)</em>}</span>
              <span data-testid="cart-shipping">€ {shipping.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold text-[#2C2C2C] text-base pt-2">
              <span>Totale</span>
              <span data-testid="cart-total">€ {total.toFixed(2)}</span>
            </div>
          </div>

          <form onSubmit={placeOrder} className="mt-10" data-testid="checkout-form">
            <h2 className="font-display text-xl text-[#2C2C2C] text-center mb-6">Dati per la spedizione</h2>
            <div className="space-y-4">
              <input required data-testid="checkout-name" placeholder="Nome e cognome *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
              <input required type="email" data-testid="checkout-email" placeholder="Email *" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
              <input data-testid="checkout-phone" placeholder="Telefono" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
              <input required data-testid="checkout-address" placeholder="Indirizzo *" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
              <div className="grid grid-cols-2 gap-4">
                <input required data-testid="checkout-city" placeholder="Città *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
                <input required data-testid="checkout-zip" placeholder="CAP *" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors" />
              </div>
              <textarea data-testid="checkout-notes" placeholder="Note (facoltativo)" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] transition-colors resize-none" />
            </div>
            <button
              type="submit"
              data-testid="checkout-submit"
              disabled={placing}
              className="mt-8 w-full gold-gradient text-white py-4 text-[12px] font-semibold tracking-wide-spaced uppercase hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
            >
              {placing ? "Invio in corso..." : "Conferma ordine"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
